import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useChildTickets } from '../../../hooks/index.js';
import type { Project } from '../../../types/project.js';

/** @graph-connects none */
export const BACKLOG_BASE = 'https://air-closet.backlog.jp/view/';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクトステータス（完了/処理中/未対応）に応じたBadgeバリアント色を決定
 * @graph-connects none
 */
export function getStatusVariant(status: string): 'success' | 'warning' | 'info' | 'default' {
  switch (status) {
    case '完了':
      return 'success';
    case '処理中':
      return 'info';
    case '未対応':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * 子チケット一覧（比率バー + テーブル）
 *
 * `ChildTicketsModal` と、案件詳細ページのダッシュボードタブの両方から使う共通部分。
 * モーダルは開いているときだけ取得したいので `enabled` で制御できる（タブ側は常に enabled）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクトのGen3子チケット一覧を比率バー付きテーブルで表示する共通セクション
 * @graph-connects useChildTickets [calls] 指定プロジェクトの子チケットデータを取得
 */
export function ChildTicketsSection({
  project,
  enabled = true,
}: {
  project: Project;
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useChildTickets(enabled ? project.id : null);

  const completionRatio =
    project.childTicketCount > 0
      ? (project.completedChildCount / project.childTicketCount) * 100
      : 0;
  const processingRatio =
    project.childTicketCount > 0
      ? (project.processingChildCount / project.childTicketCount) * 100
      : 0;

  return (
    <div className="space-y-4">
      {/* 比率バー */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#16a34a' }} />
          <span className="text-sm text-[var(--color-text-secondary)]">
            {t('projects.completed')} {completionRatio.toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3b82f6' }} />
          <span className="text-sm text-[var(--color-text-secondary)]">
            {t('projects.processing')} {processingRatio.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="h-3 bg-[var(--color-bg-base)] rounded-full overflow-hidden flex">
        <div style={{ width: `${String(completionRatio)}%`, backgroundColor: '#16a34a' }} />
        <div style={{ width: `${String(processingRatio)}%`, backgroundColor: '#3b82f6' }} />
      </div>

      {/* 子チケット一覧 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner" />
        </div>
      ) : data && data.data.length > 0 ? (
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[var(--color-bg-surface)]">
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-3 text-[var(--color-text-muted)]">
                  {t('projects.fields.issueKey')}
                </th>
                <th className="text-left py-2 px-3 text-[var(--color-text-muted)]">
                  {t('projects.fields.summary')}
                </th>
                <th className="text-left py-2 px-3 text-[var(--color-text-muted)]">
                  {t('projects.fields.assignee')}
                </th>
                <th className="text-right py-2 px-3 text-[var(--color-text-muted)]">
                  {t('projects.fields.points')}
                </th>
                <th className="text-left py-2 px-3 text-[var(--color-text-muted)]">
                  {t('projects.fields.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((ticket) => (
                <tr
                  key={ticket.id}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-3 text-[var(--color-accent-primary)] whitespace-nowrap">
                    <a
                      href={`${BACKLOG_BASE}${ticket.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {ticket.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-primary)] max-w-[300px] truncate">
                    {ticket.summary}
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                    {ticket.assignee ?? '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-[var(--color-text-secondary)]">
                    {ticket.actualPoint ?? 0}/{ticket.estimatedPoint ?? 0}
                  </td>
                  <td className="py-2 px-3">
                    <Badge variant={getStatusVariant(ticket.status)} size="sm">
                      {ticket.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center py-4 text-[var(--color-text-muted)]">
          {t('projects.noChildTickets')}
        </p>
      )}
    </div>
  );
}

/**
 * 子チケット詳細モーダル
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクトのGen3子チケット一覧をモーダルで表示。issueKey・ステータス・担当者・ポイントのテーブルを提供
 * @graph-connects ChildTicketsSection [calls] 比率バー付き子チケットテーブルの描画
 */
export function ChildTicketsModal({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}) {
  if (!project) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${project.issueKey} ${project.summary}`}
      size="full"
    >
      <ChildTicketsSection project={project} enabled={open} />
    </Modal>
  );
}
